/**
 * Session persistence — sessionStorage bridge for quiz state.
 * session-hooksSKILL1.md — Layer 4, File 3.
 *
 * Rules:
 *  3. Session state is ALWAYS mirrored to sessionStorage — survives page refresh
 *  4. sessionStorage is cleared on session complete or explicit abandon
 */
import type { PersistedQuizState, QuizState, QuizAnswer } from '@/hooks/session-types'

const STORAGE_KEY = 'nudge_quiz_session'
const MAX_AGE_MS  = 2 * 60 * 60 * 1000   // 2 hours — stale sessions discarded

export function saveSessionState(
  subject:    string,
  topic:      string,
  gradeLevel: number,
  state:      Partial<QuizState>,
  subtopic?:  string | null,
  skill?:     string | null,
  hsClass?:   string | null,
): void {
  try {
    const persisted: PersistedQuizState = {
      subject, topic, gradeLevel,
      subtopic:      subtopic      ?? null,
      skill:         skill         ?? null,
      hsClass:       hsClass       ?? null,
      currentIndex:     state.currentIndex      ?? 0,
      answers:          state.answers           ?? [],
      difficulty:       state.difficulty        ?? 'medium',
      difficultyLocked: state.difficultyLocked  ?? false,
      previousStems:    state.previousStems     ?? [],
      savedAt:          Date.now(),
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persisted))
  } catch {
    // Fail silently — private browsing may block sessionStorage
  }
}

export function loadSessionState(): PersistedQuizState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedQuizState & {
      answers?: Array<QuizAnswer & { hintUsed?: boolean }>
    }
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      clearSessionState()
      return null
    }
    // Backward-compat migration: old sessions stored hintUsed: boolean.
    // New sessions store hintsUsed: number. Normalize on restore so callers
    // always receive the current QuizAnswer shape. Explicit cast per element
    // because `tsc -b` doesn't propagate the intersection type into the callback.
    type LegacyAnswer = QuizAnswer & { hintUsed?: boolean }
    const answers: QuizAnswer[] = (parsed.answers ?? []).map((a) => {
      const legacy = a as LegacyAnswer
      return {
        ...legacy,
        hintsUsed: typeof legacy.hintsUsed === 'number' ? legacy.hintsUsed : (legacy.hintUsed ? 1 : 0),
      }
    })
    return { ...parsed, answers }
  } catch {
    return null
  }
}

export function clearSessionState(): void {
  try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* fail silently */ }
}

export function useSessionResume(): PersistedQuizState | null {
  return loadSessionState()
}

// ── Backward-compat hook for LeaveGuardDialog (Phase 4) ──────────────────────
// Phase 8 will wire this to the live useQuizState — until then it returns
// safe defaults so LeaveGuardDialog never blocks navigation.

export interface SessionStateReturn {
  status:           'idle' | 'active' | 'complete'
  answers:          unknown[]
  saveSessionState: () => void
}

export function useSessionState(): SessionStateReturn {
  return {
    status:           'idle',
    answers:          [],
    saveSessionState: () => { /* wired in Phase 8 */ },
  }
}
